import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const VoiceCommandContext = createContext(null);

// Wake word configuration
const WAKE_WORD = 'hey astraea';
const WAKE_WORD_VARIANTS = ['hey astraea', 'hey astrea', 'a astraea', 'hey australia'];

export const VoiceCommandProvider = ({ children }) => {
  const navigate = useNavigate();

  // Read mic preference from localStorage
  const getInitialMicState = () => {
    try {
      const settings = JSON.parse(localStorage.getItem('virgo_settings') || '{}');
      // If preference hasn't been asked yet, default to disabled (will be asked on first login)
      // If preference was asked, use their choice
      if (settings.astraeaMicPreferenceAsked) {
        return settings.astraeaMicAlwaysOn || false;
      }
      return false; // Default to disabled until user chooses
    } catch (e) {
      return false;
    }
  };

  // Core state
  const [isListening, setIsListening] = useState(false);
  const [isWakeWordMode, setIsWakeWordMode] = useState(true);
  const [isEnabled, setIsEnabled] = useState(getInitialMicState);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [lastCommand, setLastCommand] = useState(null);
  const [commandHistory, setCommandHistory] = useState([]);
  const [recognitionSupported, setRecognitionSupported] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [listeningMode, setListeningMode] = useState('idle'); // idle, wake-word, command, continuous

  // Refs
  const recognitionRef = useRef(null);
  const wakeWordRecognitionRef = useRef(null);
  const commandsRef = useRef([]);
  const isSpaceHeldRef = useRef(false);
  const feedbackTimeoutRef = useRef(null);

  // Callbacks for external integrations (e.g., opening Astraea bubble)
  const callbacksRef = useRef({
    onAstraeaWake: null,
    onAstraeaQuestion: null,
  });

  // Show feedback message
  const showFeedback = useCallback((message, type = 'info', duration = 2000) => {
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }
    setFeedbackMessage({ message, type });
    feedbackTimeoutRef.current = setTimeout(() => {
      setFeedbackMessage(null);
    }, duration);
  }, []);

  // Register navigation commands
  const registerNavigationCommands = useCallback(() => {
    const navCommands = [
      { pattern: /^(go to|open|show|navigate to) (the )?(dashboard|home)$/i, path: '/', description: 'Go to Dashboard' },
      { pattern: /^(go to|open|show|navigate to) (the )?(mail|email|inbox)$/i, path: '/mail', description: 'Go to Mail' },
      { pattern: /^(go to|open|show|navigate to) (the )?(cases|case)$/i, path: '/cases', description: 'Go to Cases' },
      { pattern: /^(go to|open|show|navigate to) (the )?(contacts|people)$/i, path: '/contacts', description: 'Go to Contacts' },
      { pattern: /^(go to|open|show|navigate to) (the )?(calendar|schedule|events)$/i, path: '/calendar', description: 'Go to Calendar' },
      { pattern: /^(go to|open|show|navigate to) (the )?(documents|files|docs)$/i, path: '/documents', description: 'Go to Documents' },
      { pattern: /^(go to|open|show|navigate to) (the )?(triage|queue)$/i, path: '/triage', description: 'Go to Triage' },
      { pattern: /^(go to|open|show|navigate to) (the )?(intake|intakes)$/i, path: '/intake', description: 'Go to Intake' },
      { pattern: /^(go to|open|show|navigate to) (the )?(invoices|billing)$/i, path: '/invoices', description: 'Go to Invoices' },
      { pattern: /^(go to|open|show|navigate to) (the )?(accounting|finances)$/i, path: '/accounting', description: 'Go to Accounting' },
      { pattern: /^(go to|open|show|navigate to) (the )?(payroll)$/i, path: '/payroll', description: 'Go to Payroll' },
      { pattern: /^(go to|open|show|navigate to) (the )?(sigil|chat|messages)$/i, path: '/sigil', description: 'Go to Sigil' },
      { pattern: /^(go to|open|show|navigate to) (the )?(settings|preferences)$/i, path: '/settings', description: 'Go to Settings' },
      { pattern: /^(go to|open|show|navigate to) (the )?(admin|administration)$/i, path: '/admin', description: 'Go to Admin' },
      { pattern: /^(go to|open|show|navigate to) (the )?(signatures|e-signatures|esignatures)$/i, path: '/signatures', description: 'Go to Signatures' },
      { pattern: /^(go to|open|show|navigate to) (the )?(phone|voip|calls)$/i, path: '/voip', description: 'Go to Phone' },
      { pattern: /^(go to|open|show|navigate to) (the )?(dictate|dictation)$/i, path: '/dictate', description: 'Go to Dictate' },
      { pattern: /^(go to|open|show|navigate to) (the )?(law library|library|research)$/i, path: '/law-library', description: 'Go to Law Library' },
    ];

    return navCommands.map((cmd, idx) => ({
      id: `nav-${idx}`,
      pattern: cmd.pattern,
      handler: () => {
        navigate(cmd.path);
        showFeedback(`Navigating to ${cmd.description.replace('Go to ', '')}`, 'success');
      },
      module: 'navigation',
      description: cmd.description,
    }));
  }, [navigate, showFeedback]);

  // Initialize built-in commands
  useEffect(() => {
    const builtInCommands = [
      ...registerNavigationCommands(),
      // Help command
      {
        id: 'help',
        pattern: /^(help|what can (i|you) say|voice commands|commands)$/i,
        handler: () => {
          showFeedback('Say "Go to [page]" to navigate, or "Hey Astraea" to chat', 'info', 4000);
        },
        module: 'system',
        description: 'Show help',
      },
      // Stop listening
      {
        id: 'stop-listening',
        pattern: /^(stop listening|stop|cancel|nevermind|never mind)$/i,
        handler: () => {
          stopListening();
          showFeedback('Voice commands paused', 'info');
        },
        module: 'system',
        description: 'Stop listening',
      },
    ];

    commandsRef.current = builtInCommands;
  }, [registerNavigationCommands, showFeedback]);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.log('Speech recognition not supported');
      setRecognitionSupported(false);
      return;
    }

    setRecognitionSupported(true);

    // Main recognition for commands
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }

      setInterimTranscript(interim);

      if (final) {
        setTranscript(final);
        processCommand(final.trim().toLowerCase());
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');

      // Restart wake word listening if in wake word mode
      if (isWakeWordMode && isEnabled && listeningMode !== 'continuous') {
        startWakeWordListening();
      }
    };

    recognition.onerror = (event) => {
      console.log('Speech recognition error:', event.error);
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        showFeedback('Voice error: ' + event.error, 'error');
      }
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    // Wake word recognition (continuous, low-power)
    const wakeWordRecognition = new SpeechRecognition();
    wakeWordRecognition.continuous = true;
    wakeWordRecognition.interimResults = true;
    wakeWordRecognition.lang = 'en-US';

    wakeWordRecognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript.toLowerCase().trim();

        // Check for wake word
        const hasWakeWord = WAKE_WORD_VARIANTS.some(variant =>
          transcript.includes(variant)
        );

        if (hasWakeWord) {
          console.log('Wake word detected!');
          wakeWordRecognition.stop();

          // Trigger Astraea wake callback
          if (callbacksRef.current.onAstraeaWake) {
            callbacksRef.current.onAstraeaWake();
          }

          showFeedback('Hey! How can I help?', 'success');

          // Check if there's a question after the wake word
          const afterWakeWord = transcript.split(/hey astr[ae]+a?\s*/i)[1];
          if (afterWakeWord && afterWakeWord.length > 3) {
            if (callbacksRef.current.onAstraeaQuestion) {
              callbacksRef.current.onAstraeaQuestion(afterWakeWord);
            }
          }

          return;
        }
      }
    };

    wakeWordRecognition.onend = () => {
      // Restart wake word listening if still enabled
      if (isWakeWordMode && isEnabled && !isListening) {
        setTimeout(() => {
          if (isWakeWordMode && isEnabled) {
            try {
              wakeWordRecognition.start();
            } catch (e) {
              // Already started or other error
            }
          }
        }, 100);
      }
    };

    wakeWordRecognition.onerror = (event) => {
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.log('Wake word recognition error:', event.error);
      }
    };

    wakeWordRecognitionRef.current = wakeWordRecognition;

    // Start wake word listening on mount
    if (isEnabled && isWakeWordMode) {
      startWakeWordListening();
    }

    return () => {
      recognition.abort();
      wakeWordRecognition.abort();
    };
  }, []);

  // Start wake word listening
  const startWakeWordListening = useCallback(() => {
    if (!wakeWordRecognitionRef.current || !isEnabled) return;

    try {
      wakeWordRecognitionRef.current.start();
      setListeningMode('wake-word');
    } catch (e) {
      // Already started
    }
  }, [isEnabled]);

  // Process a voice command
  const processCommand = useCallback((text) => {
    console.log('Processing command:', text);

    // Check against registered commands
    for (const command of commandsRef.current) {
      if (command.pattern.test(text)) {
        console.log('Command matched:', command.description);
        setLastCommand({ text, command: command.description, timestamp: new Date() });
        setCommandHistory(prev => [...prev.slice(-9), { text, command: command.description, timestamp: new Date() }]);
        command.handler(text);
        return true;
      }
    }

    // Check for Astraea questions
    const astraeaMatch = text.match(/^(ask astraea|astraea)\s+(.+)$/i);
    if (astraeaMatch && callbacksRef.current.onAstraeaQuestion) {
      callbacksRef.current.onAstraeaQuestion(astraeaMatch[2]);
      showFeedback('Asking Astraea...', 'info');
      return true;
    }

    // No command matched
    showFeedback(`Didn't understand: "${text}"`, 'warning');
    return false;
  }, [showFeedback]);

  // Start listening for commands
  const startListening = useCallback(() => {
    if (!recognitionRef.current || !isEnabled) return;

    // Stop wake word listening
    if (wakeWordRecognitionRef.current) {
      try {
        wakeWordRecognitionRef.current.stop();
      } catch (e) {}
    }

    try {
      recognitionRef.current.start();
      setIsListening(true);
      setListeningMode('command');
      setTranscript('');
      setInterimTranscript('');
    } catch (e) {
      console.log('Could not start recognition:', e);
    }
  }, [isEnabled]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
    }
    setIsListening(false);
    setListeningMode('idle');

    // Resume wake word if enabled
    if (isWakeWordMode && isEnabled) {
      setTimeout(startWakeWordListening, 500);
    }
  }, [isWakeWordMode, isEnabled, startWakeWordListening]);

  // Start continuous listening mode
  const startContinuousListening = useCallback(() => {
    if (!recognitionRef.current || !isEnabled) return;

    // Stop wake word listening
    if (wakeWordRecognitionRef.current) {
      try {
        wakeWordRecognitionRef.current.stop();
      } catch (e) {}
    }

    // Configure for continuous
    recognitionRef.current.continuous = true;

    try {
      recognitionRef.current.start();
      setIsListening(true);
      setListeningMode('continuous');
      showFeedback('Continuous listening active', 'success');
    } catch (e) {
      console.log('Could not start continuous recognition:', e);
    }
  }, [isEnabled, showFeedback]);

  // Register a command from external components
  const registerCommand = useCallback((command) => {
    const id = command.id || `cmd-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newCommand = { ...command, id };
    commandsRef.current = [...commandsRef.current, newCommand];
    return id;
  }, []);

  // Unregister a command
  const unregisterCommand = useCallback((id) => {
    commandsRef.current = commandsRef.current.filter(cmd => cmd.id !== id);
  }, []);

  // Register callbacks
  const registerCallback = useCallback((name, callback) => {
    callbacksRef.current[name] = callback;
  }, []);

  // Toggle voice commands enabled
  const toggleEnabled = useCallback(() => {
    setIsEnabled(prev => {
      const newValue = !prev;
      if (!newValue) {
        // Disable: stop all recognition
        if (recognitionRef.current) {
          try { recognitionRef.current.stop(); } catch (e) {}
        }
        if (wakeWordRecognitionRef.current) {
          try { wakeWordRecognitionRef.current.stop(); } catch (e) {}
        }
        setIsListening(false);
        setListeningMode('idle');
      } else {
        // Enable: start wake word listening
        if (isWakeWordMode) {
          startWakeWordListening();
        }
      }
      return newValue;
    });
  }, [isWakeWordMode, startWakeWordListening]);

  // Set enabled state directly (for preference prompt)
  const setEnabled = useCallback((enabled) => {
    if (enabled) {
      setIsEnabled(true);
      if (isWakeWordMode) {
        startWakeWordListening();
      }
    } else {
      // Disable: stop all recognition
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
      if (wakeWordRecognitionRef.current) {
        try { wakeWordRecognitionRef.current.stop(); } catch (e) {}
      }
      setIsListening(false);
      setListeningMode('idle');
      setIsEnabled(false);
    }
  }, [isWakeWordMode, startWakeWordListening]);

  // Global hotkey handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isEnabled || !recognitionSupported) return;

      // Ignore if focused on input/textarea
      const tagName = document.activeElement?.tagName?.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || document.activeElement?.isContentEditable) return;

      // Space hold (push-to-talk)
      if (e.code === 'Space' && !e.repeat && !isSpaceHeldRef.current) {
        e.preventDefault();
        isSpaceHeldRef.current = true;
        startListening();
      }

      // Ctrl+Shift+V toggle continuous
      if (e.ctrlKey && e.shiftKey && e.code === 'KeyV') {
        e.preventDefault();
        if (listeningMode === 'continuous') {
          stopListening();
        } else {
          startContinuousListening();
        }
      }
    };

    const handleKeyUp = (e) => {
      // Release space - stop listening and process
      if (e.code === 'Space' && isSpaceHeldRef.current) {
        e.preventDefault();
        isSpaceHeldRef.current = false;
        if (isListening && listeningMode === 'command') {
          stopListening();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isEnabled, recognitionSupported, isListening, listeningMode, startListening, stopListening, startContinuousListening]);

  // Get all available commands
  const getCommands = useCallback(() => {
    return commandsRef.current.map(cmd => ({
      id: cmd.id,
      description: cmd.description,
      module: cmd.module,
    }));
  }, []);

  const value = {
    // State
    isListening,
    isEnabled,
    isWakeWordMode,
    transcript,
    interimTranscript,
    lastCommand,
    commandHistory,
    recognitionSupported,
    feedbackMessage,
    listeningMode,

    // Methods
    startListening,
    stopListening,
    startContinuousListening,
    toggleEnabled,
    setEnabled,
    registerCommand,
    unregisterCommand,
    registerCallback,
    getCommands,
    setIsWakeWordMode,
  };

  return (
    <VoiceCommandContext.Provider value={value}>
      {children}
    </VoiceCommandContext.Provider>
  );
};

export const useVoiceCommands = () => {
  const context = useContext(VoiceCommandContext);
  if (!context) {
    throw new Error('useVoiceCommands must be used within a VoiceCommandProvider');
  }
  return context;
};

export default VoiceCommandContext;
